"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodeEdgeMapper = void 0;
const tslib_1 = require("tslib");
const core_services_1 = require("@iotv/core-services");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const NodeListFilter_1 = require("../../../util/dynamodb/NodeListFilter");
const lambda_1 = require("../../../util/lambda");
const identity_1 = require("../../../util/objects/identity");
const TypeHelpers_1 = require("../../../util/TypeHelpers");
const enumerableDecorator_1 = require("../enumerableDecorator");
const NodeEdgeMapperUtils_1 = require("./NodeEdgeMapperUtils");
const debug = lambda_1.DEBUG_OVERRIDE || (lambda_1.NOT_LAMBDA && false);
/**
 * @class NodeEdgeMapper provides double indexed store of nodes and edges to facilitate hierachy and aggregation functions
 * NodeEdgeMapper must hold contract that pkMapped[sk] === skMapped[pk] ie objects are referenced in maps.
 */
class NodeEdgeMapper {
    /** TODO this must be refactored as objects references are not correct, must construct from SK with PK being referenced from SK */
    constructor(nodeEdgeMapperZombie) {
        this.skMapped = {};
        this.pkMapped = {};
        /** returns a hash of this */
        this.getHash = () => (0, iotv_v3_types_1.getObjectHash)(this);
        /** axisNode item used to idenfify a single item from which other items are referenced ( like a star graph ), as opposed to apexNodes which are nodes from which other items descend */
        this.axisNode = undefined;
        this.setAxisNode = (item) => (this.axisNode = item);
        this.getAxisNode = () => this.axisNode;
        this.extractAxisNodeEdgeMapper = () => this.extractSingleNodeEdgeMapper(this.axisNode);
        this.extractSingleNodeEdgeMapper = (node) => {
            const axisNEM = new NodeEdgeMapper();
            if (node) {
                axisNEM.loadItems([node, ...this.getChildEdges(node), ...this.getChildNodes(node), ...this.getParentEdges(node), ...this.getParentNodes(node)]);
            }
            return axisNEM;
        };
        /** simple Object.assign of dehydrahed NodeEdgeMapper without counts will result in bad count of zero */
        /** returns item if exists if not undefined */
        this.getItem = (item) => this.skMapped[item.sk]?.[item.pk];
        /** loads a single edge or node, affects count */
        this.loadItem = (item) => {
            if (!this.pkMapped[item.pk]) {
                this.pkMapped[item.pk] = {};
            }
            this.pkMapped[item.pk][item.sk] = item;
            if (!this.skMapped[item.sk]) {
                this.skMapped[item.sk] = {};
            }
            if ((0, iotv_v3_types_1.isNode)(item) && !this.skMapped[item.sk][item.pk]) {
                this.nodeCount++;
            }
            if ((0, iotv_v3_types_1.isEdge)(item) && !this.skMapped[item.sk][item.pk]) {
                this.edgeCount++;
            }
            this.skMapped[item.sk][item.pk] = item;
        };
        /** updates or creates item */
        this.upsert = (item) => {
            this.loadItem({ ...this.getItem(item), ...item });
        };
        /**
         * Removes item from nodeEdgeMapper, affects count, if the item is a node, all the edges are removed as well. If the item is an edge, only that edge is removed
         */
        this.removeItem = (item) => {
            let removedItems = [];
            if (this.itemExists(item)) {
                if ((0, iotv_v3_types_1.isNode)(item)) {
                    const edges = [...this.getChildEdges(item), ...this.getParentEdges(item)];
                    for (let edge of edges) {
                        removedItems.push(...this.removeItem(edge));
                    }
                }
                delete this.skMapped[item.sk][item.pk];
                delete this.pkMapped[item.pk][item.sk];
                removedItems.push(item);
                if (Object.keys(this.skMapped[item.sk]).length === 0) {
                    delete this.skMapped[item.sk];
                }
                if (Object.keys(this.pkMapped[item.pk]).length === 0) {
                    delete this.pkMapped[item.pk];
                }
                if ((0, iotv_v3_types_1.isNode)(item)) {
                    this.nodeCount--;
                }
                if ((0, iotv_v3_types_1.isEdge)(item)) {
                    this.edgeCount--;
                }
            }
            return removedItems;
        };
        /** Removes ONLY the node from the nem, leaving edges that potentially go nowhere.
         * This is desireable in some circumstances.
         * Used in partnerRoleQuery to remove the wrong user node from a fetched nem but retain edges
         * WARN: You probably want to use removeItem instead
         */
        this.removeNode = (node) => {
            let removedNodes = [];
            if (this.itemExists(node)) {
                delete this.skMapped[node.sk][node.pk];
                delete this.pkMapped[node.pk][node.sk];
                removedNodes.push(node);
                if (Object.keys(this.skMapped[node.sk]).length === 0) {
                    delete this.skMapped[node.sk];
                }
                if (Object.keys(this.pkMapped[node.pk]).length === 0) {
                    delete this.pkMapped[node.pk];
                }
                if ((0, iotv_v3_types_1.isNode)(node)) {
                    this.nodeCount--;
                }
                if ((0, iotv_v3_types_1.isEdge)(node)) {
                    this.edgeCount--;
                }
            }
            return removedNodes;
        };
        this.itemNotUndefined = (item) => item;
        /** Load Items into Node Edge Mapper
         * @param {T[]} items - list of items to add
         */
        this.loadItems = (items) => items.filter(core_services_1.existsFilter).forEach(this.loadItem);
        this.upsertItems = (items) => items.forEach(this.upsert);
        /** gets union, ie merges with another nodeEdgeMapper, mutates but does not return thiss */
        this.union = (nodeEdgeMapper) => {
            const items = nodeEdgeMapper.getContainedNodesAndEdges();
            this.loadItems(items);
        };
        this.getPrimaryItem = (sk) => this.skMapped[sk]?.[sk];
        /**
         * Get all of the primaryItems
         */
        this.getContainedItems = () => Object.keys(this.skMapped).map(this.getPrimaryItem).filter(this.itemNotUndefined);
        /**
         *  Get all the parentEdges for each primaryItem - slow for large collections
         */
        this.getContainedEdgesSlow = () => this.getContainedItems()
            .map(this.getParentEdges)
            .reduce((a, c) => {
            c.forEach((cx) => {
                if (!a.find((ax) => ax.sk === cx.sk && ax.pk === cx.pk)) {
                    a.push(cx);
                }
            });
            return a;
        }, []);
        /** gets target node from source node and edge, and vice versa */
        this.getOtherNode = (firstNode, edge) => {
            let otherNodeSk = 'never';
            if ([edge.sk, edge.pk].includes(firstNode.sk)) {
                if (firstNode.sk === edge.sk) {
                    otherNodeSk = edge.pk;
                }
                else if (firstNode.sk === edge.pk) {
                    otherNodeSk = edge.sk;
                }
            }
            return this.skMapped[otherNodeSk]?.[otherNodeSk];
        };
        this.getNodesByPath = (source, path) => {
            let sourceNodes = [source];
            for (let step of path) {
                sourceNodes = this.getAdjacentNodesFromMultiSource(sourceNodes, step);
            }
            return sourceNodes;
        };
        /** Execute a path query on this NodeEdgeMapper contents returning all nodes and edges in the path from items, including filter support
         * This is potentially replaced by getTraversal ( note getTraversal takes single item )
         */
        this.queryByGraphSteps = (items, queryPath) => {
            const resultNodeEdgeMapper = new NodeEdgeMapper();
            const queriedItems = queryPath.reduce((a, pathstep) => {
                const iterationARes = a.reduce((b, item) => {
                    const { nodes, edges } = this.getAdjacentNodesAndEdges(item, pathstep);
                    /** very large collections will error Array.push with  Maximum call stack size exceeded */
                    try {
                        if (nodes.length < 50000) {
                            b.push(...nodes, ...edges);
                        }
                        else {
                            nodes.forEach((node) => b.push(node));
                        }
                    }
                    catch (e) {
                        console.log(`queryByGraphSteps iterationARes reduce failed with ${nodes.length} nodes ${edges.length} edges with: ${e.message}`);
                    }
                    return b;
                }, []);
                try {
                    if (iterationARes.length < 50000) {
                        a.push(...iterationARes);
                    }
                    else {
                        iterationARes.forEach((node) => a.push(node));
                    }
                }
                catch (e) {
                    console.log(`queryByGraphSteps queriedItems reduce failed with ${iterationARes.length} items with: ${e.message}`);
                }
                a.push(...iterationARes);
                return a;
            }, items);
            resultNodeEdgeMapper.loadItems(queriedItems);
            return resultNodeEdgeMapper;
        };
        /** Gets adjancent edges selected by adjacenyType, objectTypeId and edgeTypeId. This DOES NOT INCLUDE the initial item and will not return pseudo attributes ( like DeviceConfig ) */
        this.getAdjacentEdges = (item, edgeParams) => {
            const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams;
            let allEdges = [];
            if (adjacencyType === iotv_v3_types_1.AdjacentType.CHILD) {
                allEdges = this.getChildEdges(item);
            }
            else if (adjacencyType === iotv_v3_types_1.AdjacentType.PARENT) {
                allEdges = this.getParentEdges(item);
            }
            let edges = allEdges.filter((edge) => !edgeTypeId || edge.edgeTypeId === edgeTypeId);
            /** Filter on edge attribute filters */
            if (edgeParams.edgeAttributeFilters) {
                //debug && console.log('getEdges', 'Edge Attribute Filters Present', edgeAttributeFilters)
                //debug && console.log('getEdges', 'Unfiltered Edges', `(${res.data.Count})`, res.data.Items.map((e) => `${e.pk}-${e.sk}`).join(', '))
                const filteredEdges = (0, NodeListFilter_1.filterEdgesByEdgeAttributes)({
                    edges: edges,
                    edgeAttributeFilters: edgeParams.edgeAttributeFilters,
                    groupSetOperation: iotv_v3_types_1.LogicalOperatorType.AND,
                });
                edges = filteredEdges;
                //res.data.Items = filteredEdges
                //res.data.Count = filteredEdges.length
                debug && console.log('getEdges', 'Filtered Edges', `(${filteredEdges.length})`, filteredEdges.map((e) => `${e.pk}-${e.sk}`).join(', '));
            }
            return edges;
        };
        /** returns pseudo attributes like DeviceConfig */
        this.getPseudoAttributes = (item, type) => {
            return Object.entries(this.skMapped)
                .reduce((A, [K, C]) => {
                A.push(...Object.entries(C).reduce((a, c) => {
                    const [k, possibleEdge] = c;
                    if (k === item.sk && (0, iotv_v3_types_1.isEdge)(possibleEdge)) {
                        const otherNode = this.getOtherNode(item, possibleEdge);
                        if (!otherNode) {
                            a.push(possibleEdge);
                        }
                        debug && console.log(k, `other node`, (0, TypeHelpers_1.getMinimalVOB)(possibleEdge), otherNode);
                    }
                    return a;
                }, []));
                return A;
            }, [])
                .filter((item) => !type || item.type === type);
        };
        /** returns all pseudoAttributes */
        this.getContainedPseudoAttributes = () => Object.values(this.skMapped).reduce((a, c) => {
            const y = Object.values(c).filter(core_services_1.existsFilter).filter(iotv_v3_types_1.isPseudoAttribute);
            a.push(...y);
            return a;
        }, []);
        /** Gets adjacent nodes and edges selected by adjacenyType, objectTypeId and edgeTypeId. This DOES NOT INCLUDE the initial item. initial use in UI to select from applicationData */
        this.getAdjacentNodesAndEdges = (item, edgeParams) => {
            const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams;
            let adjacentEdges = this.getAdjacentEdges(item, edgeParams);
            const edgeNodes = adjacentEdges
                .map((edge) => [edge, this.getOtherNode(item, edge)]) // this probably should/could have a predicate on edgeTypeId here
                .filter(([edge, node]) => node?.type === objectTypeId);
            // TODO: edge filters!!!
            let nodes = [];
            let edges = [];
            edgeNodes.forEach(([edge, node]) => {
                ;
                (edges.push(edge), nodes.push(node));
            });
            if (edgeParams.nodeFilters) {
                const { filteredNodes, nodeFilteredEdges } = (0, NodeListFilter_1.applyNodeFilter)({ nodeFilterGroups: edgeParams.nodeFilters, nodes, edges });
                nodes = filteredNodes;
                edges = nodeFilteredEdges;
            }
            return { nodes, edges };
        };
        /** Gets all contents directly from pkMapped - 2026 04 use in prefrence to getContainedNodesAndEdges where pseudo attributes are needed */
        this.getAllContents = () => Object.values(this.pkMapped).reduce((a, c) => {
            a.push(...Object.values(c).filter(core_services_1.existsFilter));
            return a;
        }, []);
        /** @Beta ( 2024 04 - used in UI customer selector) gets all nodes and edges from multi step traversal, returns NodeEdgeMapper with traversal including source item */
        this.getTravesal = (item, queryPath) => {
            const nem = new NodeEdgeMapper();
            nem.loadItem(item);
            queryPath.reduce((lastNodes, edgeParams) => {
                const nextNodes = [];
                lastNodes.forEach((n) => {
                    const { nodes, edges } = this.getAdjacentNodesAndEdges(n, edgeParams);
                    nem.loadItems([...nodes, ...edges]);
                    nodes.forEach((nx) => {
                        if (!nextNodes.find((ny) => (0, iotv_v3_types_1.objectsMatch)(ny, nx))) {
                            nextNodes.push(nx);
                        }
                    });
                });
                return nextNodes;
            }, [item]);
            return nem;
        };
        /** applies branching query definition to this nodeEdgeMapper
         * tests for this are at file:///./../../../../../../Utilities/test/TestData/getNestedEntityGroups.test.ts
         * and file:///./../../../../../../iotv/datastore/test/GraphQuery/Groups/Descending/groupHierarchyDescending.test.ts
         * If the nodeEdgeMapper is populated from a branching query definition, applying the same definition should give exactly the same result
         * @param items - The item to start the query from. Recursive calls pass all items, initial call should just be context item
         * @param results - not usually supplied by consumer, it is populated in recursion
         */
        this.applyBranchingQuery = (items, query, results) => {
            if (!results) {
                // on first call setup results and add itesm
                results = new NodeEdgeMapper();
                results.loadItems(items);
            }
            const { path, subQueries } = query;
            const thisStepResults = path.reduce((lastNodes, edgeParams) => {
                debug && console.log(`edgeParams`, edgeParams);
                const nextNodes = [];
                /** special case where query has SUBJECT, eg in apex query */
                if (edgeParams.adjacencyType === iotv_v3_types_1.AdjacentType.SUBJECT) {
                    nextNodes.push(...lastNodes);
                }
                else {
                    lastNodes.forEach((n) => {
                        const { nodes, edges } = this.getAdjacentNodesAndEdges(n, edgeParams);
                        debug && console.log(`adj res`, nodes);
                        results.loadItems([...nodes, ...edges]);
                        nodes.forEach((nx) => {
                            if (!nextNodes.find((ny) => (0, iotv_v3_types_1.objectsMatch)(ny, nx))) {
                                nextNodes.push(nx);
                            }
                        });
                    });
                }
                return nextNodes;
            }, items);
            if (thisStepResults.length > 0) {
                for (let subQuery of subQueries) {
                    results.union(this.applyBranchingQuery(thisStepResults, subQuery));
                }
            }
            return results;
        };
        /**
         * Gets adjacent nodes, selected by {@link AdjacentType}, `objectTypeId` and `edgeTypeId`
         *
         * This **DOES NOT INCLUDE** the initial item */
        this.getAdjacentNodes = (item, edgeParams) => {
            const { objectTypeId, adjacencyType, edgeTypeId } = edgeParams;
            const adjacentEdges = this.getAdjacentEdges(item, edgeParams);
            const edgeNodePairs = adjacentEdges
                .map((edge) => [edge, this.getOtherNode(item, edge)])
                .filter(([edge, node]) => objectTypeId === '*' || node?.type === objectTypeId);
            let nodes = edgeNodePairs.filter(([edge, node]) => node !== undefined).map(([edge, node]) => node);
            if (edgeParams.nodeFilters) {
                const { filteredNodes } = (0, NodeListFilter_1.applyNodeFilter)({ nodeFilterGroups: edgeParams.nodeFilters, edges: [], nodes });
                nodes = filteredNodes;
            }
            return nodes;
        };
        this.getAdjacentNodesFromMultiSource = (items, edgeParams) => {
            let nodes = [];
            for (let item of items) {
                nodes = nodes.concat(this.getAdjacentNodes(item, edgeParams));
            }
            return nodes;
        };
        /** used by get contained edges, this does not get child edges for an item */
        this.getEdgesForItem = (sk) => this.skMapped[sk]
            ? Object.entries(this.skMapped[sk]).reduce((a, [pk, ob]) => {
                if (pk !== sk) {
                    if (ob && !(0, iotv_v3_types_1.isPseudoAttribute)(ob)) {
                        a.push(ob);
                    }
                }
                return a;
            }, [])
            : [];
        /**
         *  Get all the parentEdges for each primaryItem ( 15x faster )
         */
        this.getContainedEdges = () => Object.keys(this.skMapped).reduce((a, c) => {
            a = [...a, ...this.getEdgesForItem(c)];
            return a;
        }, []);
        /** get edge between items */
        this.getEdgeBetween = (itemA, itemB) => {
            let edge = null;
            if (itemA.sk !== itemB.sk) {
                const canonicalOut = this.skMapped[itemA.sk]?.[itemB.sk];
                const canonicalIn = this.skMapped[itemB.sk]?.[itemA.sk];
                if (canonicalIn && canonicalOut) {
                    !lambda_1.IS_FARGATE && console.log(`WARN getEdgeBetween interconected nodes`, { itemA: itemA.sk, itemB: itemB.sk, canonicalOut, canonicalIn });
                }
                else if (!canonicalIn && !canonicalOut) {
                    !lambda_1.IS_FARGATE && console.log(`WARN getEdgeBetween no edge between`, { itemA: itemA.sk, itemB: itemB.sk, edges: this.getEdgesForItem(itemA.sk) });
                }
                else {
                    debug && console.log(`getEdgeBetween`, { itemA, itemB, canonicalOut, canonicalIn });
                }
                edge = canonicalOut ?? canonicalIn ?? null;
            }
            return edge;
        };
        /**
         * Get all nodes and edges
         */
        this.getContainedNodesAndEdges = () => [...this.getContainedItems(), ...this.getContainedEdges()];
        this.isApexNode = (item) => this.skMapped[item.sk] && Object.keys(this.skMapped[item.sk]).length === 1 && this.skMapped[item.sk][item.sk] !== undefined;
        /** gets all nodes with only children */
        this.getApexNodes = () => this.getContainedItems().filter(this.isApexNode);
        /** handing node is one with no child related items */
        this.isHangingNode = (item) => this.pkMapped[item.sk] && Object.keys(this.pkMapped[item.sk]).length === 1 && this.pkMapped[item.sk][item.sk] !== undefined;
        /** gets all handing nodes */
        this.getHangingNodes = () => this.getContainedItems().filter(this.isHangingNode);
        /** test whether item is an edge and has missing source or target nodes */
        this.isUnterminatedEdge = (item) => this.skMapped[item.sk][item.pk] !== undefined && (!this.skMapped[item.pk]?.[item.pk] || !this.skMapped[item.sk]?.[item.sk]);
        /** gets all unterminated edges, either no source or target item exists in this*/
        this.getUnterminatedEdges = () => this.getContainedEdges().filter(this.isUnterminatedEdge);
        /** tests wether node is not connected to any other node in the collection */
        this.isOrphanNode = (item) => this.isHangingNode(item) && this.isApexNode(item);
        /** returns nodes not connected to any other node in the collection */
        this.getOrphanedNodes = () => this.getContainedItems().filter(this.isOrphanNode);
        this.getChildEdges = (item) => Object.values(this.pkMapped[item.sk] ?? {}).filter((child) => child && child.sk !== item.sk);
        this.getChildNodes = (item) => this.getChildEdges(item)
            .map((item) => this.getPrimaryItem(item.sk))
            .filter(this.itemNotUndefined);
        this.getParentEdges = (item) => Object.values(this.skMapped[item.sk] ?? {}).filter((child) => child && child.pk !== item.sk);
        this.getParentNodes = (item) => this.getParentEdges(item)
            .map((item) => this.getPrimaryItem(item.pk))
            .filter(this.itemNotUndefined);
        /** does the node or edge exist in this */
        this.itemExists = (item) => {
            return !!this.skMapped[item.sk]?.[item.pk] && !!this.pkMapped[item.pk]?.[item.sk];
        };
        /** applies provided function to each handing node then to each successive parent node, providing the array of previous child results, used in aggregation up hierarchy */
        this.applyRecursivelyFromHangingNodes = async (fn, apexNode) => {
            const lowerResults = await Promise.all(this.getChildNodes(apexNode).map(async (item) => await this.applyRecursivelyFromHangingNodes(fn, item)));
            const result = await fn(apexNode, lowerResults);
            return result;
        };
        /** used by getDecedents and getAscendents */
        this.iterate = ({ fn, items, maxIterations, iteration = 0 }) => {
            const combinedResults = [];
            const iterationResult = fn(items);
            if (iterationResult.length > 0 && (!maxIterations || iteration < maxIterations)) {
                const nextResults = this.iterate({ fn, items: iterationResult, maxIterations, iteration: iteration + 1 });
                nextResults.forEach((r) => combinedResults.push(r));
            }
            iterationResult.forEach((r) => combinedResults.push(r));
            return combinedResults;
        };
        /** returns decendent nodes not including initial items,
         * TODO add edgeTypeId filter
         */
        this.getDecendents = (items, maxIterations) => this.iterate({
            fn: (items) => {
                const itemChildren = items
                    .map((item) => this.getChildNodes(item))
                    .reduce((a, cs) => {
                    cs.forEach((c) => a.push(c));
                    return a;
                }, []);
                return itemChildren;
            },
            items,
            maxIterations,
        });
        /** Returns ascendent nodes (not edges). Does not return initial items.
         * TODO add edgeTypeId filter
         */
        this.getAscendents = (items, maxIterations) => this.iterate({
            fn: (items) => {
                const itemParents = items
                    .map((item) => this.getParentNodes(item))
                    .reduce((a, cs) => {
                    cs.forEach((c) => a.push(c));
                    return a;
                }, []);
                return itemParents;
            },
            items,
            maxIterations,
        });
        /** Returns the ascending nodes and edges from, but not including, startNode.
         * @param {T} startNode - The node to begin ascending from. Not included in result.
         * @param {string[]} [allowedEdgeTypes] - A list of edgeTypeIds that will be ascended. Defaults to any and all if undefined
         * The edgeTypeIdFilter is done by and 'includes' not an 'equals'
         * This differs from getAscendents in that edges are returned
         */
        this.getAscendingNodesAndEdges = (startNode, allowedEdgeTypes) => {
            //let currentAscendingNode: T | undefined = startNode;
            const ascendingNodesAndEdges = [];
            const seenNodes = [`${startNode.pk}${startNode.sk}`]; // Prevent loops, which idk if they're possible
            const ascensionQueue = [startNode];
            do {
                const curNode = ascensionQueue.shift();
                if (!curNode)
                    continue;
                const parentEdges = this.getParentEdges(curNode).filter((edge) => !allowedEdgeTypes || allowedEdgeTypes.includes(edge.edgeTypeId));
                parentEdges.forEach((parentEdge) => {
                    ascendingNodesAndEdges.push(parentEdge);
                    const ascendantNode = this.getOtherNode(curNode, parentEdge);
                    if (!ascendantNode)
                        return;
                    const ascendantId = `${ascendantNode.pk}${ascendantNode.sk}`;
                    if (!seenNodes.find((sn) => sn === ascendantId)) {
                        ascendingNodesAndEdges.push(ascendantNode);
                        ascensionQueue.push(ascendantNode);
                        seenNodes.push(ascendantId);
                    }
                });
            } while (ascensionQueue.length > 0);
            return ascendingNodesAndEdges;
        };
        /** returns array of nodes by type */
        this.getNodesOfType = (typeId) => this.getContainedItems().filter((item) => item.type === typeId);
        /** returns map of node types with count */
        this.containedTypes = () => {
            const containedTypes = {};
            Object.keys(this.skMapped).forEach((k) => {
                const t = k.split(':')[0];
                if (!containedTypes[t]) {
                    containedTypes[t] = 1;
                }
                else {
                    containedTypes[t]++;
                }
            });
            return containedTypes;
        };
        /** returns number of nodes, optionally filtered by type, may be expensive hence nodeCount, edgeCount */
        this.countNodes = (type) => Object.keys(this.skMapped).filter((k) => !type || k.startsWith(type)).length;
        /** returns number of edges and nodes */
        this.countItems = (type) => Object.keys(this.skMapped).filter((k) => !type || k.startsWith(type)).length;
        /**
         * returns random node or edge, primarily for testing
         */
        this.pickRandomOne = () => {
            const pickRandomEntry = (m) => {
                const keys = Object.keys(m);
                return m[keys[Math.floor(Math.random() * keys.length)]];
            };
            return pickRandomEntry(pickRandomEntry(this.skMapped));
        };
        this.pickRandomNode = () => {
            const nodes = this.getContainedItems();
            return nodes[Math.floor(Math.random() * nodes.length)];
        };
        this.pickRandomEdge = () => {
            const edges = this.getContainedEdges();
            return edges[Math.floor(Math.random() * edges.length)];
        };
        /** changes the id of a node and its references in any edge connected to it */
        this.changeIdentity = (item, newId) => {
            const replacementItems = [];
            if (newId && (0, iotv_v3_types_1.isValidBusinessObject)(item)) {
                let reKeyedItemsCount = 0, removedItemsCount = 0;
                const reKeyedItem = (0, identity_1.reKeyItem)(item, newId);
                reKeyedItemsCount++;
                replacementItems.push(reKeyedItem);
                const childEdges = this.getChildEdges(item); // pk is item.pk, id is not item.id
                const parentEdges = this.getParentEdges(item); // sk is item.sk, id is item.id
                const reKeyedParentEdge = (0, identity_1.reKeyItem)(item, newId);
                parentEdges.forEach((item) => {
                    if (item.id) {
                        replacementItems.push(reKeyedParentEdge);
                    }
                    reKeyedItemsCount++;
                });
                childEdges.forEach((edge) => {
                    replacementItems.push({ ...edge, pk: reKeyedItem.sk });
                    reKeyedItemsCount++;
                });
                [item, ...childEdges, ...parentEdges].forEach((item) => {
                    this.removeItem(item);
                    removedItemsCount++;
                });
                /** add new items to nodeEdgeMapper */
                this.loadItems(replacementItems);
                debug && console.log(`rekeyed ${reKeyedItemsCount}, removed ${removedItemsCount}, loaded ${replacementItems.length}`);
            }
            else {
                console.log('No id or not validBusinessObject');
            }
        };
        /** clone to the level of the SK PK maps, does not clone objects so be careful of intent */
        this.clone = () => {
            const clone = new NodeEdgeMapper();
            clone.loadItems(this.getContainedNodesAndEdges());
            return clone;
        };
        /** gets nodes and edges in this nodeEdgeMapper that are NOT in the comparable nodeEdgeMapper */
        this.getMissingNodesAndEdgesInOther = (comparable) => {
            const thisNodesAndEdges = this.getContainedNodesAndEdges();
            const nodes = [];
            const edges = [];
            thisNodesAndEdges.forEach((item) => {
                if (!comparable.itemExists(item)) {
                    if ((0, iotv_v3_types_1.isNode)(item)) {
                        nodes.push(item);
                    }
                    if ((0, iotv_v3_types_1.isEdge)(item)) {
                        edges.push(item);
                    }
                }
            });
            return { nodes, edges };
        };
        this.getSummary = () => {
            const pseudos = [];
            const isNotPseuodFilter = (0, core_services_1.getRejectionBucketingFilter)({ filter: (item) => !(0, iotv_v3_types_1.isPseudoAttribute)(item), rejectionsBucket: pseudos });
            const unterminatedEdges = this.getUnterminatedEdges().filter(isNotPseuodFilter);
            return {
                nodeCount: this.nodeCount,
                edgeCount: this.edgeCount,
                pseudoAttributes: pseudos.length,
                apexNodes: this.getApexNodes().length,
                unterminatedEdges: unterminatedEdges.length,
                orphanedNodes: this.getOrphanedNodes().length,
                byType: { ...this.containedTypes(), ...(0, identity_1.summarizeCoreApplicationObjects)(pseudos) },
            };
        };
        if (nodeEdgeMapperZombie) {
            this.skMapped = nodeEdgeMapperZombie.skMapped;
            this.pkMapped = (0, NodeEdgeMapperUtils_1.invertSkMapped)(nodeEdgeMapperZombie.skMapped);
            this.queryHashes = nodeEdgeMapperZombie.queryHashes;
            this.nodeCount = this.countNodes();
            this.edgeCount = this.getContainedEdges().length;
        }
        else {
            this.nodeCount = 0;
            this.edgeCount = 0;
        }
    }
}
exports.NodeEdgeMapper = NodeEdgeMapper;
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getHash", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "setAxisNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAxisNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "extractAxisNodeEdgeMapper", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false)
    /** creates a nodeEdgeMapper with the adjacent nodes and edges to context object */
    ,
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "extractSingleNodeEdgeMapper", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getItem", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "loadItem", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "upsert", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "removeItem", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "removeNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "itemNotUndefined", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "loadItems", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "upsertItems", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "union", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getPrimaryItem", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getContainedItems", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getContainedEdgesSlow", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getOtherNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getNodesByPath", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "queryByGraphSteps", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAdjacentEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getPseudoAttributes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getContainedPseudoAttributes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAdjacentNodesAndEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAllContents", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getTravesal", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "applyBranchingQuery", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAdjacentNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAdjacentNodesFromMultiSource", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getEdgesForItem", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getContainedEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getEdgeBetween", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getContainedNodesAndEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "isApexNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getApexNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "isHangingNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getHangingNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "isUnterminatedEdge", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getUnterminatedEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "isOrphanNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getOrphanedNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getChildEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getChildNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getParentEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getParentNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "itemExists", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "applyRecursivelyFromHangingNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "iterate", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getDecendents", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAscendents", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getAscendingNodesAndEdges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getNodesOfType", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "containedTypes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "countNodes", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "countItems", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "pickRandomOne", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "pickRandomNode", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "pickRandomEdge", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "changeIdentity", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "clone", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getMissingNodesAndEdgesInOther", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], NodeEdgeMapper.prototype, "getSummary", void 0);
